// =============================================================================
// BP-07-03: SaveButton Component
// =============================================================================

import { useState } from 'react';
import { Bookmark, Loader2 } from 'lucide-react';
import { useToggleSaveResource } from '@/features/resources/hooks/useSavedResources';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface SaveButtonProps {
  resourceId: string;
  isSaved?: boolean;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
  onToggle?: (resourceId: string, isSaved: boolean) => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function SaveButton({
  resourceId,
  isSaved: initialSaved = false,
  variant = 'default',
  size = 'md',
  showLabel = false,
  className,
  onToggle,
}: SaveButtonProps) {
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [isAnimating, setIsAnimating] = useState(false);
  const { toggle, isLoading } = useToggleSaveResource();

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    // Optimistic update
    const newSaved = !isSaved;
    setIsSaved(newSaved);
    setIsAnimating(true);

    try {
      await toggle(resourceId, isSaved);
      onToggle?.(resourceId, newSaved);
    } catch (error) {
      // Revert on error
      setIsSaved(isSaved);
    }

    // Reset animation after delay
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Size classes
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Variant classes
  const variantClasses = {
    default: cn(
      'bg-white/90 backdrop-blur-sm border border-warm-gray/10',
      'hover:bg-white hover:shadow-soft',
      isSaved && 'bg-sage/10 border-sage/20'
    ),
    ghost: cn(
      'bg-white/80 backdrop-blur-sm',
      'hover:bg-white',
      isSaved && 'bg-sage/20'
    ),
    outline: cn(
      'border-2 border-warm-gray/20',
      'hover:border-sage hover:bg-sage/5',
      isSaved && 'border-sage bg-sage/10'
    ),
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        'rounded-lg transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-sage/20',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeClasses[size],
        variantClasses[variant],
        showLabel && 'flex items-center gap-2',
        className
      )}
      aria-label={isSaved ? 'Remove from saved' : 'Save resource'}
      aria-pressed={isSaved}
    >
      {isLoading ? (
        <Loader2 className={cn(iconSizes[size], 'animate-spin text-warm-gray')} />
      ) : (
        <Bookmark
          className={cn(
            iconSizes[size],
            'transition-all duration-200',
            isSaved
              ? 'fill-sage text-sage'
              : 'text-warm-gray hover:text-sage',
            isAnimating && 'scale-125'
          )}
        />
      )}
      {showLabel && (
        <span
          className={cn(
            'text-sm font-medium',
            isSaved ? 'text-sage-dark' : 'text-navy'
          )}
        >
          {isSaved ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}

// -----------------------------------------------------------------------------
// SaveButtonText - Full text variant
// -----------------------------------------------------------------------------

interface SaveButtonTextProps extends Omit<SaveButtonProps, 'variant' | 'showLabel'> {
  savedText?: string;
  unsavedText?: string;
}

export function SaveButtonText({
  resourceId,
  isSaved: initialSaved = false,
  size = 'md',
  savedText = 'Saved',
  unsavedText = 'Save for later',
  className,
  onToggle,
}: SaveButtonTextProps) {
  const [isSaved, setIsSaved] = useState(initialSaved);
  const { toggle, isLoading } = useToggleSaveResource();

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    const newSaved = !isSaved;
    setIsSaved(newSaved);

    try {
      await toggle(resourceId, isSaved);
      onToggle?.(resourceId, newSaved);
    } catch (error) {
      setIsSaved(isSaved);
    }
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        'rounded-lg font-medium transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-sage/20',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'flex items-center gap-2',
        sizeClasses[size],
        isSaved
          ? 'bg-sage/10 text-sage-dark border border-sage/20 hover:bg-sage/20'
          : 'bg-white border border-warm-gray/20 text-navy hover:border-sage hover:text-sage-dark',
        className
      )}
      aria-label={isSaved ? 'Remove from saved' : 'Save resource'}
      aria-pressed={isSaved}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Bookmark
          className={cn(
            'w-4 h-4',
            isSaved ? 'fill-sage text-sage' : ''
          )}
        />
      )}
      {isSaved ? savedText : unsavedText}
    </button>
  );
}

export default SaveButton;
